import { useEffect, useMemo, useRef, useState } from "react";
import { getMeditationRoom, postMeditationStart } from "../../../services/api";
import { useNavigate } from "react-router-dom";

export default function MeditationInside() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [room, setRoom] = useState(null);
  const audioRef = useRef(null);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [seekValue, setSeekValue] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const roomResponse = await getMeditationRoom();
        if (!isMounted) return;
        setRoom(roomResponse?.data ?? null);
      } catch (e) {
        if (!isMounted) return;
        setError("Meditasyon verileri alınamadı.");
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (room?.audio_url && audioRef.current) {
      const audio = audioRef.current;
      audio.src = room.audio_url;
      audio.preload = 'metadata';
      audio.loop = true;
      try { audio.load(); } catch (_) {}
      // Otomatik oynatma tarayıcı tarafından engellenebilir
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    }
  }, [room?.audio_url]);

  const formatTime = (totalSeconds) => {
    const total = Math.max(0, Math.floor(totalSeconds || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };
  const durationText = useMemo(() => formatTime(duration || (Number(room?.duration_minutes) || 0) * 60), [duration, room?.duration_minutes]);
  const effectiveTime = isSeeking ? seekValue : currentTime;
  const currentTimeText = useMemo(() => formatTime(effectiveTime), [effectiveTime]);

  // audio time events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const computeDuration = () => {
      const d = Number(audio.duration);
      if (Number.isFinite(d) && d > 0) {
        setDuration(d);
      } else if (room?.duration_minutes) {
        setDuration(Number(room.duration_minutes) * 60);
      }
    };

    const onLoaded = () => computeDuration();
    const onDurationChange = () => computeDuration();
    const onCanPlayThrough = () => computeDuration();
    const onTimeUpdate = () => {
      if (!isSeeking) setCurrentTime(audio.currentTime || 0);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('durationchange', onDurationChange);
    audio.addEventListener('canplaythrough', onCanPlayThrough);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    
    return () => {
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('durationchange', onDurationChange);
      audio.removeEventListener('canplaythrough', onCanPlayThrough);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioRef.current, isSeeking, room?.duration_minutes]);

  const progressPercent = useMemo(() => {
    if (!duration) return 0;
    return Math.min(100, Math.max(0, (effectiveTime / duration) * 100));
  }, [effectiveTime, duration]);

  const handleSeekStart = () => setIsSeeking(true);
  const handleSeekChange = (e) => {
    const val = Number(e.target.value);
    setSeekValue(val);
  };
  const handleSeekEnd = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = seekValue;
    }
    setCurrentTime(seekValue);
    setIsSeeking(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">Yükleniyor...</div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-red-600">{error}</p>
        <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded-lg">Geri Dön</button>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded-none">
      {room?.image_url && (
        <img src={room.image_url} alt={room.title} className="w-full h-screen object-cover" />
      )}
      <div className="absolute inset-0 bg-black/40" />

      <div className="absolute inset-0 flex flex-col p-4 text-white">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <p className="text-lg font-semibold">{room?.title}</p>
          </div>
        </div>

        <div className="mt-3 mx-10">
          <div className="mx-auto px-8 w-full max-w-xs text-center bg-white text-black text-[12px] font-semibold py-1.5 rounded-full shadow-sm">
            Sizinle Birlikte Meditasyon Yapan 11 Jodiac Kullanıcısı
          </div>
        </div>

        <div className="mt-auto pb-6">
          <div className="w-full mb-2">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={1}
              value={effectiveTime}
              onMouseDown={handleSeekStart}
              onTouchStart={handleSeekStart}
              onChange={handleSeekChange}
              onMouseUp={handleSeekEnd}
              onTouchEnd={handleSeekEnd}
              className="w-full h-2 rounded-full appearance-none cursor-pointer range-meditation"
              style={{
                background:
                  `linear-gradient(to right, #3DA7B5 ${progressPercent}%, rgba(255,255,255,0.3) ${progressPercent}%)`,
              }}
            />
          </div>
          <div className="flex justify-between text-xs opacity-90 mb-4 select-none">
            <span>{currentTimeText}</span>
            <span>{durationText}</span>
          </div>

          <div className="flex items-center justify-center gap-6">
       
            <button
              onClick={() => {
                if (audioRef.current) {
                  if (audioRef.current.paused) {
                    audioRef.current.play();
                  } else {
                    audioRef.current.pause();
                  }
                }
              }}
              className="w-14 h-14 rounded-full bg-[#3DA7B5] flex items-center justify-center shadow-lg"
            >
              {isPlaying ? (
                // Pause icon
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                // Play icon
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
        
          </div>
        </div>
      </div>

      <audio ref={audioRef} hidden loop />
    </div>
  );
}